import React, {
  memo,
  useCallback,
  useState,
  useMemo,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
  useContext,
} from 'react';
import ReactDOM from 'react-dom';
import { Rnd } from 'react-rnd';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { PrimaryButton } from '@/components/common/PrimaryButton';
// import VoiceRecorder from 'src/context/ai-assistant-v2/components/VoiceRecorder';
import useTranscribe from 'src/api/voice/useTranscribe';
import SelectDropdown, {
  SelectDropdownOption,
} from 'src/components/common/SelectDropdown';
import { AI_MODEL_OPTIONS } from 'src/types/aiModels';
import {
  ChatCircleIcon,
  MinusIcon,
  PaperPlaneRightIcon,
  DotsSixVerticalIcon,
  CircleNotchIcon,
  ArrowSquareOutIcon,
  ArrowLineLeftIcon,
  CaretDownIcon,
  CheckIcon,
  TrashIcon,
  FlowArrowIcon,
  BugIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { AuthContext } from '@/context/auth.context';
import { useViewMode } from '@/context/view-mode.context';
import { isAdmin } from '@/utils/user';

// Context for portal container (to fix dropdown issues in popup windows)
const PortalContainerContext = React.createContext<HTMLElement | null>(null);

// Portal-aware SelectDropdown that preserves all features in popup windows
const PortalAwareSelectDropdown: React.FC<
  React.ComponentProps<typeof SelectDropdown> & {
    portalContainer?: HTMLElement | null;
  }
> = ({ portalContainer, ...props }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Calculate dropdown position relative to button
  const getDropdownPosition = () => {
    if (!buttonRef.current || !portalContainer) {
      return { top: '4rem', left: '1rem', width: '20rem' };
    }

    const buttonRect = buttonRef.current.getBoundingClientRect();
    return {
      top: `${buttonRect.bottom + 4}px`, // 4px gap below button
      left: `${buttonRect.left}px`,
      width: `${buttonRect.width}px`,
    };
  };

  // Handle click outside to close dropdown in popup
  useEffect(() => {
    if (open && portalContainer) {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        if (!portalContainer.contains(target)) {
          setOpen(false);
        }
      };

      portalContainer.ownerDocument.addEventListener(
        'mousedown',
        handleClickOutside,
      );
      return () => {
        portalContainer.ownerDocument.removeEventListener(
          'mousedown',
          handleClickOutside,
        );
      };
    }
  }, [open, portalContainer]);

  // If we're in a popup window, use a custom implementation with proper portal
  if (portalContainer) {
    return (
      <div className="space-y-2 w-full">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              ref={buttonRef}
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={props.disabled}
              size={props.size === 'small' ? 'sm' : 'default'}
              className={cn(
                'justify-between font-normal w-full h-9', // Force h-9 to match buttons
                !props.value && 'text-muted-foreground',
                props.error &&
                  'border-destructive bg-destructive/5 ring-1 ring-destructive/20',
              )}
            >
              <span className="flex-1 text-left overflow-hidden">
                {props.value ? props.value.display : props.placeholder}
              </span>
              <CaretDownIcon className="h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
        </Popover>

        {/* Manual portal to popup window */}
        {open &&
          ReactDOM.createPortal(
            <div
              className={cn(
                'bg-popover text-popover-foreground z-50 rounded-md border shadow-md p-0',
                'fixed',
              )}
              style={{
                ...getDropdownPosition(),
                maxHeight: '300px',
              }}
            >
              <Command>
                <CommandInput
                  placeholder={props.searchPlaceholder || 'Search'}
                />
                <CommandList className="max-h-[250px]">
                  <CommandEmpty>No options found.</CommandEmpty>
                  <CommandGroup>
                    {props.options.map((option) => (
                      <CommandItem
                        key={option.id}
                        value={option.label}
                        onSelect={() => {
                          props.onChange(option);
                          setOpen(false);
                        }}
                        className="cursor-pointer min-h-[2.5rem] items-start py-2"
                      >
                        <CheckIcon
                          className={cn(
                            'mr-2 h-4 w-4 mt-0.5 flex-shrink-0',
                            props.value?.id === option.id
                              ? 'opacity-100'
                              : 'opacity-0',
                          )}
                        />
                        <span className="flex-1 whitespace-normal leading-tight">
                          {option.label}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </div>,
            portalContainer,
          )}
      </div>
    );
  }

  // In main window, use the regular SelectDropdown with height override
  return <SelectDropdown {...props} className={cn('h-9', props.className)} />;
};

import { SupportedGPTModels } from '../types/spreadsheetTypes';
import { WorkflowPhase } from '../types/workflowTypes';
import posthog from 'posthog-js';

// Chat dimension constants
const CHAT_DIMENSIONS = {
  WIDTH: {
    MIN: 300,
    DEFAULT: 800,
  },
  HEIGHT: {
    MIN: 400, // Increased minimum height to prevent overflow
    DEFAULT: 500,
  },
  PADDING: 24, // Consistent padding from edges
} as const;

// Custom hook for managing popup window
function usePopoutWindow() {
  const [isPopped, setIsPopped] = useState(false);
  const [popupWindow, setPopupWindow] = useState<Window | null>(null);
  const popupRef = useRef<Window | null>(null);

  const openPopup = useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) {
      // Window already exists, just focus it
      popupRef.current.focus();
      return;
    }

    const popup = window.open(
      '',
      'ai-assistant-chat',
      'width=900,height=650,scrollbars=yes,resizable=yes,status=no,toolbar=no,menubar=no',
    );

    if (popup) {
      popupRef.current = popup;
      setPopupWindow(popup);
      setIsPopped(true);

      // Set up the popup window HTML structure
      popup.document.title = 'AI Assistant';
      popup.document.head.innerHTML = `
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <title>AI Assistant</title>
      `;

      // Copy stylesheets from parent window
      const stylesheets = Array.from(
        document.head.querySelectorAll('link[rel="stylesheet"], style'),
      );
      stylesheets.forEach((sheet) => {
        const clone = sheet.cloneNode(true);
        popup.document.head.appendChild(clone);
      });

      // Create container for React content
      popup.document.body.innerHTML =
        '<div id="popup-root" style="margin: 0; padding: 0; height: 100vh; overflow: hidden;"></div>';

      // Handle popup window close
      popup.addEventListener('beforeunload', () => {
        setIsPopped(false);
        setPopupWindow(null);
        popupRef.current = null;
      });

      // Handle parent window close/reload
      window.addEventListener('beforeunload', () => {
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
        }
      });
    }
  }, []);

  const closePopup = useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    setIsPopped(false);
    setPopupWindow(null);
    popupRef.current = null;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    };
  }, []);

  return {
    isPopped,
    popupWindow,
    openPopup,
    closePopup,
  };
}

// Conversation message interface - internal to this component
interface ConversationMessage {
  message: string;
  isUser: boolean;
  timestamp: Date;
  requestStartTime?: Date;
  duration?: number;
  model?: SupportedGPTModels;
  phase?: WorkflowPhase; // Track which phase this message belongs to
}

interface AIAssistantChatV2Props {
  // Current phase context
  currentPhase: WorkflowPhase;
  isImageReviewMode?: boolean;

  // Model state (lifted up)
  selectedModel: SupportedGPTModels;
  onModelChange: (model: SupportedGPTModels) => void;

  // Orchestrator state (lifted up)
  useOrchestrator?: boolean;
  onOrchestratorChange?: (useOrchestrator: boolean) => void;

  // Test mode state (lifted up)
  useTestMode?: boolean;
  onTestModeChange?: (useTestMode: boolean) => void;

  // Loading state
  isTransforming: boolean;

  // Phase-specific request handlers (parent handles API calls)
  onImageReparseRequest?: (instructions?: string) => Promise<void>; // For Phase 0 image re-parsing
  onPreprocessingRequest: (instructions?: string) => Promise<void>;
  onMappingRequest: (instructions?: string) => Promise<void>;
  onWorkflowRequest: (instructions?: string) => Promise<void>;

  // Conversation management
  onClearConversation?: () => void;

  // Optional: inline flow controls, mirrored from the top navigator
  canProceed?: boolean;
  continueDisabledReason?: string;
  onContinue?: () => void;
  isSubmitting?: boolean;
  // Phase 3 submit gating (separate from continue logic)
  canSubmit?: boolean;
  submitDisabledReason?: string;
  // Central busy flag for gating CTAs
  isBusy?: boolean;
}

export interface AIAssistantChatV2Handle {
  appendAiMessage: (
    message: string,
    meta?: {
      requestStartTime?: Date;
      duration?: number;
      model?: SupportedGPTModels;
      phase?: WorkflowPhase;
    },
  ) => void;
  appendUserMessage: (message: string) => void;
  clearConversation: () => void;
}

// Memoized conversation message component
const ConversationMessageItem = memo(
  ({ msg, index }: { msg: ConversationMessage; index: number }) => {
    const getTimingDisplay = () => {
      if (msg.isUser) {
        return msg.timestamp.toLocaleTimeString();
      } else {
        if (msg.requestStartTime && msg.duration !== undefined) {
          const startTime = msg.requestStartTime.toLocaleTimeString();
          const endTime = msg.timestamp.toLocaleTimeString();
          // const modelInfo = msg.model ? ` (${msg.model})` : '';
          // const phaseInfo =
          //   msg.phase !== undefined ? ` [Phase ${msg.phase}]` : '';
          //return `${startTime} - ${endTime} (${msg.duration}s)${modelInfo}${phaseInfo}`;
          return `${startTime} - ${endTime} (${msg.duration}s)`;
        } else {
          return msg.timestamp.toLocaleTimeString();
        }
      }
    };

    return (
      <div
        key={`${msg.timestamp.getTime()}-${index}`}
        className={cn(
          'mb-4 flex',
          msg.isUser ? 'justify-end' : 'justify-start',
        )}
      >
        <div
          className={cn(
            'max-w-[80%] p-3 rounded-xl',
            msg.isUser
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-900 border border-gray-300',
          )}
        >
          <p
            className={cn(
              'text-sm whitespace-pre-wrap break-words',
              msg.isUser ? 'font-medium' : 'font-normal',
            )}
          >
            {msg.message}
          </p>
          <p
            className={cn(
              'text-xs opacity-80 mt-2 block',
              msg.isUser ? 'text-blue-100' : 'text-gray-500',
            )}
          >
            {getTimingDisplay()}
          </p>
        </div>
      </div>
    );
  },
);

const DISABLE_ORCHESTRATOR_TOGGLE = true;

ConversationMessageItem.displayName = 'ConversationMessageItem';

// Custom equality function for memoization
const areEqual = (
  prevProps: AIAssistantChatV2Props,
  nextProps: AIAssistantChatV2Props,
) => {
  return (
    prevProps.currentPhase === nextProps.currentPhase &&
    prevProps.isImageReviewMode === nextProps.isImageReviewMode &&
    prevProps.selectedModel === nextProps.selectedModel &&
    prevProps.onModelChange === nextProps.onModelChange &&
    prevProps.useOrchestrator === nextProps.useOrchestrator &&
    prevProps.onOrchestratorChange === nextProps.onOrchestratorChange &&
    prevProps.useTestMode === nextProps.useTestMode &&
    prevProps.onTestModeChange === nextProps.onTestModeChange &&
    prevProps.isTransforming === nextProps.isTransforming &&
    prevProps.onImageReparseRequest === nextProps.onImageReparseRequest &&
    prevProps.onPreprocessingRequest === nextProps.onPreprocessingRequest &&
    prevProps.onMappingRequest === nextProps.onMappingRequest &&
    prevProps.onWorkflowRequest === nextProps.onWorkflowRequest &&
    prevProps.onClearConversation === nextProps.onClearConversation &&
    prevProps.canProceed === nextProps.canProceed &&
    prevProps.continueDisabledReason === nextProps.continueDisabledReason &&
    prevProps.onContinue === nextProps.onContinue &&
    prevProps.isSubmitting === nextProps.isSubmitting &&
    prevProps.canSubmit === nextProps.canSubmit &&
    prevProps.submitDisabledReason === nextProps.submitDisabledReason &&
    prevProps.isBusy === nextProps.isBusy
  );
};

// Separate chat content component that can be rendered in main window or popup
const ChatContent: React.FC<
  AIAssistantChatV2Props & {
    conversation: ConversationMessage[];
    inputMessage: string;
    isCollapsed: boolean;
    onTextChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onSubmit: () => void;
    onToggleCollapse: () => void;
    onDockAndMinimize: () => void;
    onOpenPopup?: () => void;
    onClosePopup?: () => void;
    transcribeAudioLoading: boolean;
    transcribeAudioError: Error | null;
    onRecordingComplete: (audioFile: File) => void;
    getPlaceholderText: () => string;
    isPopped?: boolean;
  }
> = ({
  currentPhase,
  isImageReviewMode: _isImageReviewMode,
  selectedModel,
  onModelChange,
  useOrchestrator,
  onOrchestratorChange,
  useTestMode,
  onTestModeChange,
  isTransforming,
  onClearConversation,
  canProceed,
  continueDisabledReason,
  onContinue,
  isSubmitting,
  canSubmit,
  submitDisabledReason,
  isBusy,
  conversation,
  inputMessage,
  isCollapsed,
  onTextChange,
  onSubmit,
  onToggleCollapse,
  onDockAndMinimize,
  onOpenPopup,
  onClosePopup,
  transcribeAudioLoading,
  transcribeAudioError,
  onRecordingComplete,
  getPlaceholderText,
  isPopped = false,
}) => {
  // Portal container for fixing dropdown issues in popup windows
  const portalContainer = React.useContext(PortalContainerContext);

  // Admin check for orchestrator toggle visibility
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const showOrchestratorToggle =
    !DISABLE_ORCHESTRATOR_TOGGLE && isAdmin(currentUser, viewMode);
  const showTestModeToggle =
    isAdmin(currentUser, viewMode) && import.meta.env.DEV;

  // Model options
  const modelOptions = useMemo<SelectDropdownOption[]>(
    () => AI_MODEL_OPTIONS.map((o) => ({ ...o })),
    [],
  );

  const selectedModelOption = useMemo(
    () => modelOptions.find((option) => option.value === selectedModel) || null,
    [modelOptions, selectedModel],
  );

  const handleModelChange = useCallback(
    (option: SelectDropdownOption | null) => {
      if (option) {
        onModelChange(option.value as SupportedGPTModels);
      }
    },
    [onModelChange],
  );

  // Show collapsed FAB when collapsed (only in main window)
  if (isCollapsed && !isPopped) {
    return (
      <Button
        onClick={onToggleCollapse}
        size="lg"
        className="fixed bottom-6 right-6 z-[40] h-14 w-14 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg"
      >
        <ChatCircleIcon className="h-6 w-6" />
      </Button>
    );
  }

  // Don't render anything if collapsed in popup
  if (isCollapsed && isPopped) {
    return null;
  }

  const chatWindow = (
    <div
      style={{
        width: '100%',
        height: '100%',
        minHeight: `${CHAT_DIMENSIONS.HEIGHT.MIN}px`,
        border: '1px solid #d0d0d0',
        borderRadius: '12px',
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
      }}
    >
      <div className="p-4 pb-2">
        <div
          className={cn(
            'flex justify-between items-center mb-4',
            !isPopped && [
              'drag-handle cursor-grab select-none',
              'hover:bg-blue-50 hover:text-blue-600 active:cursor-grabbing active:bg-blue-100',
              'rounded-lg p-2 -mx-2 transition-all duration-200',
            ],
            isPopped && 'p-2 -mx-2',
          )}
        >
          <div className="flex items-center gap-2">
            {!isPopped && (
              <DotsSixVerticalIcon
                className={cn(
                  'drag-icon h-4 w-4 text-gray-400 opacity-60 transition-all duration-200',
                )}
              />
            )}
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-900">
                AI Assistant
              </h3>
              <span className="text-xs text-gray-500 opacity-60 font-mono">
                {`Press ${
                  navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'
                } + L to ${isPopped ? 'dock back' : 'toggle'}`}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="min-w-[180px]">
              {/* <PortalAwareSelectDropdown
                options={modelOptions}
                value={selectedModelOption}
                onChange={handleModelChange}
                placeholder="Select model..."
                disabled={isTransforming}
                size="small"
                fullWidth={true}
                portalContainer={portalContainer}
              /> */}
            </div>
            {showOrchestratorToggle && onOrchestratorChange && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => onOrchestratorChange(!useOrchestrator)}
                    size="icon"
                    variant="outline"
                    className={cn(
                      'h-9 w-9 flex-shrink-0 border-gray-300',
                      useOrchestrator
                        ? 'bg-purple-100 hover:bg-purple-200 border-purple-500 text-purple-700'
                        : 'bg-gray-100 hover:bg-gray-200 border-gray-300 text-gray-600',
                    )}
                    title={`Orchestrator: ${useOrchestrator ? 'ON' : 'OFF'}`}
                    disabled={isTransforming}
                  >
                    <FlowArrowIcon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {useOrchestrator
                      ? 'Using orchestrator-worker pattern'
                      : 'Using traditional workflow generation'}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
            {onClearConversation && (
              <Button
                onClick={onClearConversation}
                size="icon"
                variant="outline"
                className="h-9 w-9 flex-shrink-0 bg-gray-100 border-gray-300 hover:bg-red-100 hover:border-red-500"
                title="Clear conversation history"
                disabled={isTransforming}
              >
                <TrashIcon className="h-4 w-4 text-gray-600" />
              </Button>
            )}
            {showTestModeToggle && onTestModeChange && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => onTestModeChange(!useTestMode)}
                    size="icon"
                    variant="outline"
                    className={cn(
                      'h-9 w-9 flex-shrink-0',
                      useTestMode
                        ? 'bg-yellow-100 hover:bg-yellow-200 border-yellow-500 text-yellow-700'
                        : 'bg-gray-100 hover:bg-gray-200 border-gray-300 text-gray-600',
                    )}
                    title={`Test Mode: ${useTestMode ? 'ON' : 'OFF'}`}
                    disabled={isTransforming}
                  >
                    <BugIcon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {useTestMode
                      ? 'Using mock data (no AI calls)'
                      : 'Click to enable test mode'}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
            {!isPopped && onOpenPopup && (
              <Button
                onClick={onOpenPopup}
                size="icon"
                variant="outline"
                className="h-9 w-9 flex-shrink-0 bg-gray-100 border-gray-300 hover:bg-blue-100 hover:border-blue-500"
                title="Pop out to new window"
              >
                <ArrowSquareOutIcon className="h-4 w-4 text-gray-600" />
              </Button>
            )}
            {isPopped && onClosePopup && (
              <Button
                onClick={onClosePopup}
                size="icon"
                variant="outline"
                className="h-9 w-9 flex-shrink-0 bg-gray-100 border-gray-300 hover:bg-green-100 hover:border-green-500"
                title="Dock back to main window"
              >
                <ArrowLineLeftIcon className="h-4 w-4 text-gray-600" />
              </Button>
            )}
            <Button
              onClick={isPopped ? onDockAndMinimize : onToggleCollapse}
              size="icon"
              variant="outline"
              className="h-9 w-9 flex-shrink-0 bg-gray-100 border-gray-300 hover:bg-yellow-100 hover:border-yellow-500 hover:text-white"
              title={isPopped ? 'Minimize to main window' : 'Minimize'}
            >
              <MinusIcon className="h-5 w-5 text-gray-600 font-bold" />
            </Button>
          </div>
        </div>
      </div>

      {/* Test Mode Warning Banner */}
      {useTestMode && (
        <div className="mx-4 mb-2 p-2 bg-yellow-50 border border-yellow-300 rounded-lg flex items-center gap-2">
          <BugIcon className="h-4 w-4 text-yellow-700 flex-shrink-0" />
          <p className="text-xs text-yellow-700 font-medium">
            Test Mode Active - Using mock data (no AI calls)
          </p>
        </div>
      )}

      {/* Conversation History */}
      <div className="px-4 pb-2 relative flex-1 flex flex-col min-h-[200px]">
        {conversation.length > 0 ? (
          <Card className="p-4 bg-gray-50 border border-gray-200 flex-1 overflow-y-auto overflow-x-hidden relative min-h-[150px] max-h-full">
            {conversation.map((msg: ConversationMessage, index: number) => (
              <ConversationMessageItem
                key={`${msg.timestamp.getTime()}-${index}`}
                msg={msg}
                index={index}
              />
            ))}
          </Card>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-500 text-sm text-center px-6 min-h-[150px]">
            <p className="mb-2 font-medium text-gray-600">AI Assistant</p>
            <p className="opacity-70 mb-2 text-xs">
              {currentPhase === 1 && 'Ready to clean and preprocess your data'}
              {currentPhase === 2 &&
                'Ready to extract constants and map fields'}
              {currentPhase === 3 &&
                'Ready to generate transformation workflow'}
            </p>
            <p className="opacity-50 text-xs italic">
              {`Press ${
                navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'
              } + L to ${isPopped ? 'dock back' : 'toggle'}`}
            </p>
          </div>
        )}
      </div>

      <div className="p-4 py-6">
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Textarea
              value={inputMessage}
              onChange={onTextChange}
              placeholder={getPlaceholderText()}
              disabled={isTransforming}
              className={cn(
                'min-h-[80px] text-sm leading-relaxed resize-none',
                transcribeAudioError && 'border-red-500 bg-red-50',
              )}
            />
            {transcribeAudioError && (
              <p className="text-xs text-red-600 mt-1">
                Voice transcription error: {transcribeAudioError.message}
              </p>
            )}
          </div>
          {/* Temporarily hidden - VoiceRecorder for speech-to-text */}
          {/* <VoiceRecorder
            id="ai-assistant-v2-voice-recorder-button"
            onRecordingComplete={onRecordingComplete}
            isTranscribing={transcribeAudioLoading}
            loading={isTransforming}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            error={transcribeAudioError as any}
            size={80}
          /> */}
          <Button
            id="ai-assistant-v2-send-button"
            onClick={onSubmit}
            disabled={isTransforming}
            className="min-w-[80px] w-20 h-20 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 flex items-center justify-center p-0"
          >
            {isTransforming ? (
              <CircleNotchIcon className="h-5 w-5 text-white animate-spin" />
            ) : (
              <PaperPlaneRightIcon className="h-5 w-5 text-white" />
            )}
          </Button>
          {/* Inline Continue/Submit control with proper disabled tooltip */}
          <div className="flex flex-col gap-1">
            {(() => {
              // Phase 3: Use submit gating logic; other phases: use continue gating
              const isPhase3 = currentPhase === 3;
              const isDisabled = isPhase3
                ? !canSubmit || Boolean(isSubmitting)
                : Boolean(isSubmitting) ||
                  Boolean(isBusy) ||
                  canProceed === false;

              const tooltipTitle = (() => {
                if (!isDisabled) return '';
                if (isPhase3) {
                  // Phase 3 submit button
                  if (submitDisabledReason) return submitDisabledReason;
                  if (isSubmitting) return 'Submitting your data. Please wait.';
                } else {
                  // Other phases continue button
                  if (continueDisabledReason) return continueDisabledReason;
                  if (isSubmitting)
                    return 'Processing. Please wait to continue.';
                }
                return '';
              })();

              return (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <PrimaryButton
                        color={isDisabled ? 'secondary' : 'primary'}
                        disabled={isDisabled}
                        onClick={onContinue}
                        className={`min-w-20 h-20 rounded-lg flex items-center justify-center px-4 ${
                          isDisabled
                            ? 'bg-gray-400 text-gray-600'
                            : 'bg-green-600 hover:bg-green-700 text-white'
                        }`}
                      >
                        {isPhase3
                          ? isSubmitting
                            ? 'Submitting...'
                            : 'Submit'
                          : 'Continue'}
                      </PrimaryButton>
                    </span>
                  </TooltipTrigger>
                  {tooltipTitle && (
                    <TooltipContent>
                      <p>{tooltipTitle}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );

  if (isPopped) {
    // In popup window, render without the outer positioning container
    return chatWindow;
  }

  // In main window, render with Rnd for drag/resize
  return chatWindow;
};

const AIAssistantChatV2Inner = (
  {
    currentPhase,
    isImageReviewMode,
    selectedModel,
    onModelChange,
    useOrchestrator,
    onOrchestratorChange,
    useTestMode,
    onTestModeChange,
    isTransforming,
    onImageReparseRequest,
    onPreprocessingRequest,
    onMappingRequest,
    onWorkflowRequest,
    onClearConversation,
    canProceed,
    continueDisabledReason,
    onContinue,
    isSubmitting,
    canSubmit,
    submitDisabledReason,
    isBusy,
  }: AIAssistantChatV2Props,
  ref: React.Ref<AIAssistantChatV2Handle>,
) => {
  // Internal state for conversation and UI
  const [conversation, setConversation] = useState<ConversationMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(false);
  // Local loading state for immediate UI feedback
  const [isLocalLoading, setIsLocalLoading] = useState(false);

  // Popout window management
  const { isPopped, popupWindow, openPopup, closePopup } = usePopoutWindow();

  // Clear conversation when phase changes
  const clearConversation = useCallback(() => {
    setConversation([]);
  }, []);

  // Expose imperative API for parent to append/clear messages
  useImperativeHandle(
    ref,
    () => ({
      appendAiMessage: (
        message: string,
        meta?: {
          requestStartTime?: Date;
          duration?: number;
          model?: SupportedGPTModels;
          phase?: WorkflowPhase;
        },
      ) => {
        const now = new Date();
        setConversation((prev) => [
          ...prev,
          {
            message,
            isUser: false,
            timestamp: now,
            requestStartTime: meta?.requestStartTime,
            duration: meta?.duration,
            model: meta?.model ?? selectedModel,
            phase: meta?.phase ?? currentPhase,
          },
        ]);
      },
      appendUserMessage: (message: string) => {
        setConversation((prev) => [
          ...prev,
          {
            message,
            isUser: true,
            timestamp: new Date(),
            phase: currentPhase,
          },
        ]);
      },
      clearConversation: () => setConversation([]),
    }),
    [currentPhase, selectedModel],
  );

  // Phase-aware placeholder text
  const getPlaceholderText = () => {
    switch (currentPhase) {
      case 0:
        return isImageReviewMode
          ? 'Provide instructions to improve image parsing (e.g., "Focus on the table", "Include header row")...'
          : 'Ask AI to extract supplier, currency, and date range from this sheet/CSV...';
      case 1:
        return 'Describe how to clean and preprocess your data...';
      case 2:
        return 'Provide instructions for field mapping and constants extraction...';
      case 3:
        return 'Specify how to generate and execute the transformation workflow...';
      default:
        return 'Describe how you want to transform your spreadsheet data...';
    }
  };

  // Phase-aware default instructions
  const getDefaultInstructions = useCallback(() => {
    switch (currentPhase) {
      case 0:
        return isImageReviewMode
          ? 'Re-parse the image(s) with better accuracy.'
          : 'Extract supplier, currency, and overall date range (ISO).';
      case 1:
        return 'Clean and prepare the data for transformation.';
      case 2:
        return 'Extract constants and map field values.';
      case 3:
        return 'Generate and execute the transformation workflow.';
      default:
        return 'Transform this data.';
    }
  }, [currentPhase, isImageReviewMode]);

  // Main AI submit handler - routes to parent callback handlers
  const handleAiSubmit = useCallback(
    async (message: string) => {
      // Set local loading state immediately for instant UI feedback
      setIsLocalLoading(true);

      try {
        // Add user message to conversation if there's content
        if (message.length > 0) {
          const userMessage: ConversationMessage = {
            message,
            isUser: true,
            timestamp: new Date(),
            phase: currentPhase,
          };
          setConversation((prev) => [...prev, userMessage]);
        }

        // Track the request start time for conversation display
        const requestStartTime = new Date();
        const instructions = message || getDefaultInstructions();

        // Build a full conversation context to include in the request instructions
        const conversationContext = (() => {
          const lines = conversation
            .concat(
              message
                ? [
                    {
                      message,
                      isUser: true,
                      timestamp: new Date(),
                      phase: currentPhase,
                    } as ConversationMessage,
                  ]
                : [],
            )
            .map((m) => {
              const role = m.isUser ? 'User' : 'AI';
              const time = m.timestamp.toISOString();
              const phaseInfo =
                m.phase !== undefined ? ` [Phase ${m.phase}]` : '';
              return `${time} - ${role}${phaseInfo}: ${m.message}`;
            })
            .join('\n');
          return `Conversation history (most recent last):\n${lines}`;
        })();

        const fullInstructions = `${conversationContext}\n\nCurrent request: ${instructions}`;

        try {
          // Route to appropriate handler based on current phase
          switch (currentPhase) {
            case 0:
              if (onImageReparseRequest) {
                await onImageReparseRequest(fullInstructions);
              } else {
                throw new Error('Image re-parsing not available in this phase');
              }
              break;
            case 1:
              await onPreprocessingRequest(fullInstructions);
              break;
            case 2:
              await onMappingRequest(fullInstructions);
              break;
            case 3:
              await onWorkflowRequest(fullInstructions);
              break;
            default:
              throw new Error(`Invalid phase: ${currentPhase}`);
          }

          // Success message is handled by parent component with detailed results

          // Track AI interaction
          posthog.capture(`workflow_ai_phase_${currentPhase}_requested`, {
            context: 'ai_assistant_v2',
            phase: currentPhase,
          });
        } catch (error) {
          console.error(`Phase ${currentPhase} AI error:`, error);

          const endTime = new Date();
          const duration = Math.round(
            (endTime.getTime() - requestStartTime.getTime()) / 1000,
          );

          const errorMessage: ConversationMessage = {
            message: `❌ Phase ${currentPhase} failed: ${
              error instanceof Error ? error.message : 'Unknown error occurred'
            }`,
            isUser: false,
            timestamp: endTime,
            requestStartTime,
            duration,
            model: selectedModel,
            phase: currentPhase,
          };

          setConversation((prev) => [...prev, errorMessage]);
        }
      } finally {
        // Clear local loading state when done
        setIsLocalLoading(false);
      }
    },
    [
      currentPhase,
      selectedModel,
      onImageReparseRequest,
      onPreprocessingRequest,
      onMappingRequest,
      onWorkflowRequest,
      getDefaultInstructions,
      conversation,
    ],
  );

  // Calculate position with proper viewport bounds
  const getOptimalPosition = useCallback(() => {
    const padding = CHAT_DIMENSIONS.PADDING;
    let width: number = CHAT_DIMENSIONS.WIDTH.DEFAULT;
    let height: number = CHAT_DIMENSIONS.HEIGHT.DEFAULT;

    // Get current viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Ensure the chat doesn't exceed viewport dimensions
    const maxWidth = viewportWidth - padding * 2;
    const maxHeight = viewportHeight - padding * 2;

    // Adjust dimensions if they exceed viewport
    if (width > maxWidth) {
      width = Math.max(CHAT_DIMENSIONS.WIDTH.MIN, maxWidth);
    }
    if (height > maxHeight) {
      height = Math.max(CHAT_DIMENSIONS.HEIGHT.MIN, maxHeight);
    }

    // Calculate position ensuring it stays within bounds
    const x = Math.min(
      Math.max(padding, viewportWidth - width - padding),
      viewportWidth - width - padding,
    );
    const y = Math.min(
      Math.max(padding, viewportHeight - height - padding),
      viewportHeight - height - padding,
    );

    return {
      x: Math.max(0, x), // Ensure not negative
      y: Math.max(0, y), // Ensure not negative
      width,
      height,
    };
  }, []);

  // Chat window state
  const [windowState, setWindowState] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  }>(getOptimalPosition);

  const handleDragStop = useCallback(
    (_e: unknown, data: { x: number; y: number }) => {
      setWindowState((prev) => ({ ...prev, x: data.x, y: data.y }));
    },
    [],
  );

  const handleResizeStop = useCallback(
    (
      e: MouseEvent | TouchEvent,
      direction: string,
      ref: HTMLElement,
      delta: { width: number; height: number },
      position: { x: number; y: number },
    ) => {
      setWindowState({
        x: position.x,
        y: position.y,
        width: ref.offsetWidth as number,
        height: ref.offsetHeight as number,
      });
    },
    [],
  );

  // Voice transcription
  const {
    mutate: transcribeAudio,
    isLoading: transcribeAudioLoading,
    error: transcribeAudioError,
  } = useTranscribe((response) => {
    setInputMessage(response.text);
  });

  const handleRecordingComplete = useCallback(
    (audioFile: File) => {
      posthog.capture('ai_assistant_voice_recording_complete', {
        context: 'ai_assistant_v2',
        phase: currentPhase,
      });
      transcribeAudio({ audio: audioFile });
    },
    [transcribeAudio, currentPhase],
  );

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInputMessage(e.target.value);
    },
    [],
  );

  const handleSubmit = useCallback(() => {
    posthog.capture('ai_assistant_send_button_clicked', {
      context: 'ai_assistant_v2',
      phase: currentPhase,
    });
    handleAiSubmit(inputMessage.trim());
    setInputMessage('');
  }, [inputMessage, handleAiSubmit, currentPhase]);

  const toggleCollapse = useCallback(() => {
    setIsCollapsed((prev) => {
      posthog.capture('ai_assistant_toggle_minimize', {
        context: 'ai_assistant_v2',
        minimized: !prev,
      });
      return !prev;
    });
  }, []);

  // Combined function for popup: dock back + minimize
  const dockAndMinimize = useCallback(() => {
    closePopup(); // First dock back to main window
    setIsCollapsed(true); // Then minimize
    posthog.capture('ai_assistant_dock_and_minimize', {
      context: 'ai_assistant_v2',
    });
  }, [closePopup]);

  // Keyboard shortcut - different behavior based on popup state
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'l') {
        event.preventDefault();
        event.stopPropagation();

        if (isPopped) {
          // In popup window: dock back to main window
          closePopup();
        } else {
          // In main window: toggle minimize/show
          toggleCollapse();
        }
      }
    };

    // Add listener to main window
    document.addEventListener('keydown', handleKeyDown);

    // Also add listener to popup window if it exists
    if (isPopped && popupWindow) {
      popupWindow.document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (isPopped && popupWindow) {
        popupWindow.document.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [toggleCollapse, isPopped, closePopup, popupWindow]);

  // Clear conversation when phase changes (but preserve window position)
  useEffect(() => {
    clearConversation();
  }, [currentPhase, clearConversation]);

  // Clear local loading state when parent loading state becomes active
  useEffect(() => {
    if (isTransforming && isLocalLoading) {
      setIsLocalLoading(false);
    }
  }, [isTransforming, isLocalLoading]);

  // Handle window resize to maintain proper positioning within bounds
  useEffect(() => {
    const handleResize = () => {
      setWindowState((prev) => {
        const padding = CHAT_DIMENSIONS.PADDING;
        let { width, height } = prev;

        // Get viewport dimensions
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Ensure the chat doesn't exceed viewport dimensions
        const maxWidth = viewportWidth - padding * 2;
        const maxHeight = viewportHeight - padding * 2;

        // Adjust dimensions if they exceed viewport
        if (width > maxWidth) {
          width = Math.max(CHAT_DIMENSIONS.WIDTH.MIN, maxWidth);
        }
        if (height > maxHeight) {
          height = Math.max(CHAT_DIMENSIONS.HEIGHT.MIN, maxHeight);
        }

        // Ensure position stays within bounds after resize
        const maxX = viewportWidth - width - padding;
        const maxY = viewportHeight - height - padding;

        const x = Math.min(Math.max(padding, prev.x), Math.max(padding, maxX));
        const y = Math.min(Math.max(padding, prev.y), Math.max(padding, maxY));

        return {
          x: Math.max(0, x),
          y: Math.max(0, y),
          width,
          height,
        };
      });
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Combine parent loading state with local loading state for immediate feedback
  const isTransformingCombined = isTransforming || isLocalLoading;

  // Chat content props
  const chatContentProps = {
    currentPhase,
    isImageReviewMode,
    selectedModel,
    onModelChange,
    useOrchestrator,
    onOrchestratorChange,
    useTestMode,
    onTestModeChange,
    isTransforming: isTransformingCombined,
    onImageReparseRequest,
    onPreprocessingRequest,
    onMappingRequest,
    onWorkflowRequest,
    onClearConversation,
    canProceed,
    continueDisabledReason,
    onContinue,
    isSubmitting,
    canSubmit,
    submitDisabledReason,
    isBusy,
    conversation,
    inputMessage,
    isCollapsed,
    onTextChange: handleTextChange,
    onSubmit: handleSubmit,
    onToggleCollapse: toggleCollapse,
    onDockAndMinimize: dockAndMinimize,
    onOpenPopup: openPopup,
    onClosePopup: closePopup,
    transcribeAudioLoading,
    transcribeAudioError,
    onRecordingComplete: handleRecordingComplete,
    getPlaceholderText,
  };

  // When popped out, render both FAB in main window AND portal to popup window
  if (isPopped) {
    const elements = [];

    // FAB in main window
    elements.push(
      <div
        key="fab"
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 50,
        }}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={closePopup}
              size="icon"
              className="h-14 w-14 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-200"
            >
              <ChatCircleIcon className="h-6 w-6" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Bring AI Assistant back to main window</p>
          </TooltipContent>
        </Tooltip>
      </div>,
    );

    // Portal to popup window
    if (popupWindow) {
      const popupRoot = popupWindow.document.getElementById('popup-root');
      if (popupRoot) {
        elements.push(
          ReactDOM.createPortal(
            <PortalContainerContext.Provider value={popupWindow.document.body}>
              <ChatContent {...chatContentProps} isPopped={true} />
            </PortalContainerContext.Provider>,
            popupRoot,
            'popup-content',
          ),
        );
      }
    }

    return <>{elements}</>;
  }

  // Render in main window (with or without Rnd container)
  if (isCollapsed) {
    return <ChatContent {...chatContentProps} isPopped={false} />;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        zIndex: 40,
      }}
    >
      <Rnd
        size={{ width: windowState.width, height: windowState.height }}
        position={{ x: windowState.x, y: windowState.y }}
        onDragStop={handleDragStop}
        onResizeStop={handleResizeStop}
        minWidth={CHAT_DIMENSIONS.WIDTH.MIN}
        minHeight={CHAT_DIMENSIONS.HEIGHT.MIN}
        bounds="parent"
        dragHandleClassName="drag-handle"
        cancel="button, input, textarea, [data-slot='button'], [data-slot='tooltip-trigger'], .react-select"
        style={{ pointerEvents: 'auto' }}
      >
        <ChatContent {...chatContentProps} isPopped={false} />
      </Rnd>
    </div>
  );
};

const AIAssistantChatV2 = memo(
  forwardRef<AIAssistantChatV2Handle, AIAssistantChatV2Props>(
    AIAssistantChatV2Inner,
  ),
  areEqual,
);

AIAssistantChatV2.displayName = 'AIAssistantChatV2';

export default AIAssistantChatV2;
